import React, { useEffect, useState } from 'react';
import { fetchWrapper } from 'utils/apiFetch';
import { useSession } from 'hooks/useSession';

const fmt  = (n) => '₹ ' + (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => (parseInt(n) || 0).toLocaleString('en-IN');

const SERVICE_COLORS = {
  'Wallet Settlement': '#00d4aa', 'AEPS': '#4f8ef7', 'DMT': '#f78c6c',
  'Recharge': '#3fb950', 'MATM': '#c792ea', 'BBPS': '#d29922',
  'CMS': '#58a6ff', 'PAN Card': '#ff7b72', 'Insurance': '#79c0ff',
  'Travel': '#ffa657', 'Fastag': '#56d364', 'LIC': '#bc8cff',
};
const getColor = (name) => SERVICE_COLORS[name] || '#8b949e';

function StatCard({ label, value, icon, color = '#00d4aa', sub }) {
  return (
    <div style={{
      background: '#031a3f', border: '1px solid #30363d', borderRadius: 12,
      padding: 18, position: 'relative', overflow: 'hidden',
      transition: 'border-color 0.2s, transform 0.2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color }} />
      <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 20, opacity: 0.3 }}>{icon}</div>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8b949e', marginBottom: 10 }}>{label}</div>
      <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 600, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#8b949e', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function SummaryPill({ label, value, icon, color = '#00d4aa', bg = 'rgba(0,212,170,0.12)' }) {
  return (
    <div style={{ background: '#031a3f', border: '1px solid #30363d', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 2 }}>{label}</div>
        <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 600, color }}>{value}</div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8b949e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      {children}
      <div style={{ flex: 1, height: 1, background: '#30363d' }} />
    </div>
  );
}

function Td({ children, color }) {
  return (
    <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontWeight: 600, color: color || '#e6edf3', whiteSpace: 'nowrap' }}>
      {children}
    </td>
  );
}

function DistributorDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [currentTime, setCurrentTime]     = useState(new Date());
  const { userid } = useSession();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetchWrapper(`https://pro.mokshiq.in/dashboard_api?id=${userid}`);
        setDashboardData(response);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userid]);

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#ffff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
      </div>
    );
  }

  if (error) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#f85149' }}>⚠️ Failed to load dashboard: {error}</div>;
  }

  const d = dashboardData;

  // ── New API field mapping ─────────────────────────────────────────────────
  // Distributor (role_id=4) downline = retailer only
  // d.wallets            → { wallet_amount, main_wallet, credit_wallet, hold_amount }
  // d.downline_wallets   → { retailer: { count, wallet_total } }
  // d.member_stats       → { retailer: { total, new_today } }
  // d.txn_today          → { success:{count,amount}, failed:{count,amount}, pending:{count,amount} }
  // d.recharge           → { success:{count,amount}, failed:{count,amount}, pending:{count,amount} }
  // d.summary            → { total_amount, total_entries, total_net_commission, total_net_charge, ... }
  // d.services           → [{ service, amount, entries, in_commission, out_commission, net_commission, ... }]
  // d.pending_load_requests → number

  const wallets  = d?.wallets              || {};
  const dlw      = d?.downline_wallets     || {};
  const members  = d?.member_stats         || {};
  const txnToday = d?.txn_today            || {};
  const recharge = d?.recharge             || {};
  const s        = d?.summary              || {};
  const services = d?.services             || [];
  const pending  = d?.pending_load_requests || 0;

  // Distributor downline: retailer only
  const retPool    = dlw.retailer    || {};
  const retMembers = members.retailer || {};

  const totals = services.reduce((acc, item) => ({
    entries:        acc.entries        + (item.entries        || 0),
    amount:         acc.amount         + (item.amount         || 0),
    in_commission:  acc.in_commission  + (item.in_commission  || 0),
    out_commission: acc.out_commission + (item.out_commission || 0),
    net_commission: acc.net_commission + (item.net_commission || 0),
    in_charge:      acc.in_charge      + (item.in_charge      || 0),
    out_charge:     acc.out_charge     + (item.out_charge     || 0),
    net_charge:     acc.net_charge     + (item.net_charge     || 0),
  }), { entries:0, amount:0, in_commission:0, out_commission:0, net_commission:0, in_charge:0, out_charge:0, net_charge:0 });

  return (
    <main style={{ fontFamily: "'Space Grotesk', sans-serif", background: '#ffff', minHeight: '100vh', padding: 20, color: '#e6edf3' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #30363d' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px' }}>
          Distributor <span style={{ color: '#00d4aa' }}>Dashboard</span>
        </h1>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#8b949e', background: '#1c2330', border: '1px solid #30363d', borderRadius: 6, padding: '5px 12px' }}>
          {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      </div>

      {/* Pending load request alert */}
      {pending > 0 && (
        <div style={{ background: 'rgba(210,153,34,0.12)', border: '1px solid #d29922', borderRadius: 10, padding: '12px 18px', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: '#d29922', fontWeight: 700, fontSize: 13 }}>
            🔔 {pending} pending wallet load request{pending > 1 ? 's' : ''}
          </span>
          <a href="/load_wallet" style={{ color: '#d29922', fontSize: 12, fontWeight: 600, textDecoration: 'none', border: '1px solid #d29922', borderRadius: 6, padding: '4px 12px' }}>
            Review →
          </a>
        </div>
      )}

      {/* Wallet + clock + earning cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 24 }}>
        {/* Own wallets — new API: d.wallets */}
        <StatCard label="Wallet Balance"  value={fmt(wallets.wallet_amount)} icon="💼" color="#00d4aa" sub="Available funds" />
        <StatCard label="Main Wallet"     value={fmt(wallets.main_wallet)}   icon="🏦" color="#4f8ef7" sub="Commission wallet" />
        {wallets.credit_wallet > 0 && (
          <StatCard label="Credit Wallet" value={fmt(wallets.credit_wallet)} icon="💳" color="#c792ea" sub="Credited balance" />
        )}

        {/* Retailer pool — only downline for Distributor */}
        {retPool.count > 0 && (
          <StatCard label="Retailer Pool" value={fmt(retPool.wallet_total)} icon="🏪" color="#3fb950" sub={`${fmtN(retPool.count)} retailers`} />
        )}

        {/* Clock */}
        <div style={{ background: '#031a3f', border: '1px solid #30363d', borderRadius: 12, padding: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8b949e', marginBottom: 10 }}>🕐 Live Clock</div>
          <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 600, color: '#4f8ef7', letterSpacing: 2 }}>
            {currentTime.toLocaleTimeString('en-IN')}
          </div>
          <div style={{ fontSize: 11, color: '#8b949e', marginTop: 6 }}>
            {currentTime.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
          </div>
        </div>

        <StatCard label="Today's Earning" value={fmt(s.total_net_commission)} icon="📈" color="#f78c6c" sub="Net commission today" />
        <StatCard label="Today's Sales"   value={fmt(s.total_amount)}         icon="💹" color="#3fb950" sub="Total txn amount" />
      </div>

      {/* Today's txn status */}
      <SectionTitle>Today's Transactions</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Success', data: txnToday.success, color: '#3fb950', icon: '✅' },
          { label: 'Failed',  data: txnToday.failed,  color: '#f85149', icon: '❌' },
          { label: 'Pending', data: txnToday.pending, color: '#d29922', icon: '⏳' },
        ].map(({ label, data, color, icon }) => (
          <div key={label} style={{ background: '#031a3f', border: '1px solid #30363d', borderRadius: 12, padding: '16px 18px', borderLeft: `3px solid ${color}` }}>
            <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 6, fontWeight: 600 }}>{icon} {label}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color, marginBottom: 3 }}>{fmtN(data?.count)}</div>
            <div style={{ fontSize: 11, color: '#8b949e', fontFamily: 'monospace' }}>{fmt(data?.amount)}</div>
          </div>
        ))}
      </div>

      {/* Recharge today */}
      <SectionTitle>Recharge Today</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <SummaryPill label="Recharge Success" value={`${fmtN(recharge.success?.count)} · ${fmt(recharge.success?.amount)}`} icon="✅" color="#3fb950" bg="rgba(63,185,80,0.12)" />
        <SummaryPill label="Recharge Failed"  value={`${fmtN(recharge.failed?.count)}  · ${fmt(recharge.failed?.amount)}`}  icon="❌" color="#f85149" bg="rgba(248,81,73,0.12)" />
        <SummaryPill label="Recharge Pending" value={`${fmtN(recharge.pending?.count)} · ${fmt(recharge.pending?.amount)}`} icon="⏳" color="#d29922" bg="rgba(210,153,34,0.12)" />
      </div>

      {/* Member stats — Retailer only for Distributor */}
      {retMembers.total > 0 && (
        <>
          <SectionTitle>Member Stats</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            <div style={{ background: '#031a3f', border: '1px solid #30363d', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: '#8b949e', marginBottom: 6 }}>🏪 Retailers</div>
              <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: '#3fb950', marginBottom: 2 }}>{fmtN(retMembers.total)}</div>
              <div style={{ fontSize: 11, color: '#3fb950' }}>+{retMembers.new_today} today</div>
            </div>
          </div>
        </>
      )}

      {/* Summary pills */}
      <SectionTitle>Summary Totals</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <SummaryPill label="Total Txns"       value={fmtN(s.total_entries)}        icon="🔢" color="#00d4aa" bg="rgba(0,212,170,0.12)" />
        <SummaryPill label="Total Amount"     value={fmt(s.total_amount)}           icon="💰" color="#4f8ef7" bg="rgba(79,142,247,0.12)" />
        <SummaryPill label="Commission (CR)"  value={fmt(s.total_in_commission)}    icon="📥" color="#3fb950" bg="rgba(63,185,80,0.12)" />
        <SummaryPill label="Commission (DR)"  value={fmt(s.total_out_commission)}   icon="📤" color="#f85149" bg="rgba(248,81,73,0.12)" />
        <SummaryPill label="Net Commission"   value={fmt(s.total_net_commission)}   icon="⬆️" color="#3fb950" bg="rgba(63,185,80,0.12)" />
        <SummaryPill label="Net Charge"       value={fmt(s.total_net_charge)}       icon="📊" color="#c792ea" bg="rgba(199,146,234,0.12)" />
      </div>

      {/* Service table */}
      <SectionTitle>Service Summary</SectionTitle>
      <div style={{ background: '#031a3f', border: '1px solid #30363d', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#1c2330', borderBottom: '1px solid #30363d' }}>
              <tr>
                {['Service','Txns','Amount','Comm CR','Comm DR','Net Comm','Charge CR','Charge DR','Net Charge'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8b949e', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#8b949e', padding: 32 }}>No service data for today</td></tr>
              ) : services.map((item, idx) => {
                const color = getColor(item.service);
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #30363d' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#1c2330'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
                        {item.service}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ background: 'rgba(79,142,247,0.12)', color: '#4f8ef7', border: '1px solid rgba(79,142,247,0.25)', padding: '3px 8px', borderRadius: 999, fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}>
                        {fmtN(item.entries)}
                      </span>
                    </td>
                    <Td>{fmt(item.amount)}</Td>
                    <Td color="#3fb950">{fmt(item.in_commission)}</Td>
                    <Td color={item.out_commission ? '#f85149' : '#8b949e'}>{item.out_commission ? fmt(item.out_commission) : '₹ 0.00'}</Td>
                    <Td color="#3fb950">{fmt(item.net_commission)}</Td>
                    <Td color="#3fb950">{fmt(item.in_charge)}</Td>
                    <Td color={item.out_charge ? '#f85149' : '#8b949e'}>{item.out_charge ? fmt(item.out_charge) : '₹ 0.00'}</Td>
                    <Td color="#3fb950">{fmt(item.net_charge)}</Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot style={{ background: '#1c2330', borderTop: '1px solid #30363d' }}>
              <tr>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#8b949e' }}>TOTAL</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#3fb950' }}>{fmtN(totals.entries)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>{fmt(totals.amount)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#3fb950' }}>{fmt(totals.in_commission)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#f85149' }}>{fmt(totals.out_commission)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#3fb950' }}>{fmt(totals.net_commission)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#3fb950' }}>{fmt(totals.in_charge)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#f85149' }}>{fmt(totals.out_charge)}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#3fb950' }}>{fmt(totals.net_charge)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

    </main>
  );
}

export default DistributorDashboard;